import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Camera, Upload, RotateCcw, Edit3, CheckCircle2, AlertCircle, Loader2, Lock } from 'lucide-react';
import { createWorker } from 'tesseract.js';
import { toast } from 'sonner';
import { exchangeRateService, formatCurrency } from '@/utils/exchangeRates';
import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
const POPULAR_CURRENCIES = [{
  code: 'USD',
  name: 'US Dollar',
  symbol: '$',
  patterns: ['$', 'usd', 'dollar']
}, {
  code: 'EUR',
  name: 'Euro',
  symbol: '€',
  patterns: ['€', 'eur', 'euro']
}, {
  code: 'JPY',
  name: 'Japanese Yen',
  symbol: '¥',
  patterns: ['¥', 'jpy', 'yen']
}, {
  code: 'GBP',
  name: 'Pound Sterling',
  symbol: '£',
  patterns: ['£', 'gbp', 'pound']
}, {
  code: 'AUD',
  name: 'Australian Dollar',
  symbol: 'A$',
  patterns: ['a$', 'aud']
}, {
  code: 'CAD',
  name: 'Canadian Dollar',
  symbol: 'C$',
  patterns: ['c$', 'cad']
}, {
  code: 'CHF',
  name: 'Swiss Franc',
  symbol: 'Fr',
  patterns: ['chf', 'fr', 'franc']
}, {
  code: 'CNY',
  name: 'Chinese Yuan',
  symbol: '¥',
  patterns: ['cny', 'yuan', 'rmb']
}, {
  code: 'HKD',
  name: 'Hong Kong Dollar',
  symbol: 'HK$',
  patterns: ['hk$', 'hkd']
}, {
  code: 'NZD',
  name: 'New Zealand Dollar',
  symbol: 'NZ$',
  patterns: ['nz$', 'nzd']
}, {
  code: 'KRW',
  name: 'South Korean Won',
  symbol: '₩',
  patterns: ['₩', 'krw', 'won']
}, {
  code: 'INR',
  name: 'Indian Rupee',
  symbol: '₹',
  patterns: ['₹', 'inr', 'rupee']
}, {
  code: 'SGD',
  name: 'Singapore Dollar',
  symbol: 'S$',
  patterns: ['s$', 'sgd']
}];
interface ScanResult {
  detectedAmount: string;
  detectedCurrency: string;
  convertedAmount: number;
  targetCurrency: string;
  confidence: number;
}
interface CurrencyScannerProps {
  targetCurrency: string;
  onScanResult: (result: ScanResult) => void;
}
export function CurrencyScanner({
  targetCurrency,
  onScanResult
}: CurrencyScannerProps) {
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const canUseCurrencyScanner = hasFeature('budget_tracking');
  
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editCurrency, setEditCurrency] = useState('USD');
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const showUpgradePrompt = () => {
    toast.info(
      <div className="space-y-2">
        <p className="font-medium">✨ Premium Feature</p>
        <p className="text-sm">Currency scanning is available with Premium Economy or First Class Pack.</p>
        <Button 
          size="sm" 
          className="mt-2 w-full"
          onClick={() => navigate('/pricing')}
        >
          Upgrade Now
        </Button>
      </div>,
      { duration: 5000 }
    );
  };
  const startCamera = async () => {
    try {
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment'
        } // Use back camera on mobile
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Could not access camera. Please check permissions.');
      setShowCamera(false);
    }
  };
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewImage(url);
        processImage(blob);
      }
    }, 'image/jpeg', 0.8);
    stopCamera();
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewImage(url);
      processImage(file);
    }
  };
  const detectCurrencyFromText = (text: string): {
    currency: string;
    amount: string;
    confidence: number;
  } => {
    const cleanText = text.toLowerCase().replace(/\s+/g, ' ');

    // Look for currency symbols and amounts
    const patterns = [
    // Amount with currency symbol before: $123.45, €50.00
    /([€$£¥₹₩])\s*(\d+(?:[,.]\d{2,3})*(?:[,.]\d{2})?)/g,
    // Amount with currency code after: 123.45 USD, 50.00 EUR
    /(\d+(?:[,.]\d{2,3})*(?:[,.]\d{2})?)\s*(usd|eur|gbp|jpy|cny|aud|cad|chf|inr|krw|hkd|nzd|sgd)/g,
    // Currency code before amount: USD 123.45
    /(usd|eur|gbp|jpy|cny|aud|cad|chf|inr|krw|hkd|nzd|sgd)\s*(\d+(?:[,.]\d{2,3})*(?:[,.]\d{2})?)/g];
    let bestMatch = {
      currency: 'USD',
      amount: '0',
      confidence: 0
    };
    for (const pattern of patterns) {
      const matches = Array.from(cleanText.matchAll(pattern));
      for (const match of matches) {
        let currency = '';
        let amount = '';
        if (match[1] && match[2]) {
          // Symbol before amount or currency code after amount
          if (['€', '$', '£', '¥', '₹', '₩'].includes(match[1])) {
            currency = getCurrencyFromSymbol(match[1]);
            amount = match[2];
          } else {
            amount = match[1];
            currency = match[2].toUpperCase();
          }
        }
        if (currency && amount) {
          const confidence = calculateConfidence(match[0], text);
          if (confidence > bestMatch.confidence) {
            bestMatch = {
              currency,
              amount: amount.replace(/,/g, ''),
              confidence
            };
          }
        }
      }
    }
    return bestMatch;
  };
  const getCurrencyFromSymbol = (symbol: string): string => {
    const symbolMap: Record<string, string> = {
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP',
      '¥': 'JPY',
      '₹': 'INR',
      '₩': 'KRW'
    };
    return symbolMap[symbol] || 'USD';
  };
  const calculateConfidence = (match: string, fullText: string): number => {
    // Base confidence
    let confidence = 0.5;

    // Increase confidence for clear currency indicators
    if (/[€$£¥₹₩]/.test(match)) confidence += 0.3;
    if (/\d+\.\d{2}/.test(match)) confidence += 0.2; // Decimal format

    return Math.min(confidence, 1.0);
  };
  const convertCurrency = async (amount: string, fromCurrency: string, toCurrency: string): Promise<number> => {
    try {
      const rate = await exchangeRateService.getRate(fromCurrency, toCurrency);
      return parseFloat(amount) * rate;
    } catch (error) {
      console.error('Error converting currency:', error);
      return parseFloat(amount); // Fallback to original amount
    }
  };
  const processImage = async (file: Blob) => {
    setIsScanning(true);
    setScanResult(null);
    try {
      toast.info('Scanning image for currency information...');
      const worker = await createWorker('eng');
      const {
        data: {
          text
        }
      } = await worker.recognize(file);
      console.log('OCR Text:', text);
      const detected = detectCurrencyFromText(text);
      console.log('Detected:', detected);
      if (detected.amount && detected.amount !== '0') {
        const converted = await convertCurrency(detected.amount, detected.currency, targetCurrency);
        const result: ScanResult = {
          detectedAmount: detected.amount,
          detectedCurrency: detected.currency,
          convertedAmount: converted,
          targetCurrency,
          confidence: detected.confidence
        };
        setScanResult(result);
        onScanResult(result);
        toast.success('Currency detected successfully!');
      } else {
        toast.error('No currency amount detected. Try a clearer image or manual entry.');
      }
      await worker.terminate();
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };
  const handleEdit = () => {
    if (scanResult) {
      setEditAmount(scanResult.detectedAmount);
      setEditCurrency(scanResult.detectedCurrency);
    }
    setIsEditing(true);
  };
  const saveEdit = async () => {
    if (editAmount) {
      const converted = await convertCurrency(editAmount, editCurrency, targetCurrency);
      const result: ScanResult = {
        detectedAmount: editAmount,
        detectedCurrency: editCurrency,
        convertedAmount: converted,
        targetCurrency,
        confidence: 1.0
      };
      setScanResult(result);
      onScanResult(result);
      setIsEditing(false);
      toast.success('Currency updated successfully!');
    }
  };
  const rescan = () => {
    setScanResult(null);
    setPreviewImage(null);
    setIsEditing(false);
  };
  const formatCurrencyLocal = (value: number, currencyCode: string) => {
    return formatCurrency(value, currencyCode);
  };
  return <Card className="border-cyan-500/20 overflow-hidden">
      <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
              <Camera className="h-5 w-5" />
            </span>
            <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Scan & Convert Currency
            </span>
            {!canUseCurrencyScanner && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Lock className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium">✨ Premium Feature</p>
                    <p className="text-sm">Available with Premium Economy or First Class Pack. Upgrade to scan receipts and convert currencies instantly!</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Scan receipts, bills, or price tags to instantly convert currency amounts using live exchange rates
          </CardDescription>
        </CardHeader>
      </div>
      <CardContent className="space-y-6 pt-6">
        {/* Camera/Upload Controls */}
        {!showCamera && !previewImage && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button 
              onClick={canUseCurrencyScanner ? startCamera : showUpgradePrompt} 
              className="h-20 flex flex-col gap-2 relative bg-gradient-to-br from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white shadow-md shadow-cyan-500/20 border-0"
            >
              <Camera className="h-6 w-6" />
              <span className="font-medium">Use Camera</span>
              {!canUseCurrencyScanner && <Lock className="h-3 w-3 absolute top-2 right-2" />}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={canUseCurrencyScanner ? () => fileInputRef.current?.click() : showUpgradePrompt} 
              className="h-20 flex flex-col gap-2 relative border-cyan-500/30 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 hover:border-cyan-500/50"
            >
              <Upload className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
              <span className="font-medium">Upload Image</span>
              {!canUseCurrencyScanner && <Lock className="h-3 w-3 absolute top-2 right-2 text-muted-foreground" />}
            </Button>
            
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          </div>}

        {/* Camera View */}
        {showCamera && <div className="space-y-4">
            <div className="relative bg-black rounded-xl overflow-hidden ring-2 ring-cyan-500/30">
              <video ref={videoRef} autoPlay playsInline className="w-full h-64 object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="border-2 border-cyan-300 border-dashed rounded-lg w-3/4 h-3/4 flex items-center justify-center">
                  <span className="text-white text-sm bg-black/60 backdrop-blur px-3 py-1.5 rounded-full">
                    Position currency amount in frame
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={capturePhoto} className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0">
                <Camera className="h-4 w-4 mr-2" />
                Capture
              </Button>
              <Button variant="outline" onClick={stopCamera}>
                Cancel
              </Button>
            </div>
          </div>}

        {/* Image Preview & Processing */}
        {previewImage && <div className="space-y-4">
            <div className="relative">
              <img src={previewImage} alt="Scanned currency" className="w-full h-48 object-contain bg-muted rounded-xl ring-1 ring-cyan-500/20" />
              {isScanning && <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center rounded-xl">
                  <div className="text-white text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <span>Scanning...</span>
                  </div>
                </div>}
            </div>
          </div>}

        {/* Scan Results */}
        {scanResult && !isEditing && <div className="space-y-4">
            <Alert className="border-cyan-500/30 bg-cyan-50/50 dark:bg-cyan-950/20">
              <CheckCircle2 className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
              <AlertDescription>
                Currency successfully detected with {Math.round(scanResult.confidence * 100)}% confidence
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-border rounded-xl bg-card">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Detected Amount</Label>
                <div className="text-2xl font-bold mt-1">
                  {formatCurrencyLocal(parseFloat(scanResult.detectedAmount), scanResult.detectedCurrency)}
                </div>
              </div>
              
              <div className="p-4 border border-cyan-500/30 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50/50 dark:from-cyan-950/30 dark:to-teal-950/20">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Converted Amount (Live Rate)</Label>
                <div className="text-2xl font-bold mt-1 bg-gradient-to-r from-cyan-600 to-teal-600 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent">
                  {formatCurrencyLocal(scanResult.convertedAmount, scanResult.targetCurrency)}
                </div>
                <Badge variant="outline" className="text-xs mt-2 border-cyan-500/40 text-cyan-700 dark:text-cyan-300">
                  Using live market rates
                </Badge>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEdit} size="sm" className="border-cyan-500/30 hover:bg-cyan-50 dark:hover:bg-cyan-950/30">
                <Edit3 className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" onClick={rescan} size="sm" className="border-cyan-500/30 hover:bg-cyan-50 dark:hover:bg-cyan-950/30">
                <RotateCcw className="h-4 w-4 mr-2" />
                Scan Again
              </Button>
            </div>
          </div>}

        {/* Edit Mode */}
        {isEditing && <div className="space-y-4">
            <Alert className="border-cyan-500/30 bg-cyan-50/50 dark:bg-cyan-950/20">
              <AlertCircle className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
              <AlertDescription>
                Edit the detected amount and currency if needed
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)} placeholder="Enter amount" />
              </div>
              
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={editCurrency} onValueChange={setEditCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.code} - {currency.name}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>}

        {/* Instructions */}
        {!previewImage && !showCamera && <div className="p-4 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-50/60 to-teal-50/30 dark:from-cyan-950/20 dark:to-teal-950/10">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-cyan-400 to-teal-500 text-white text-xs">💡</span>
              Tips for best results
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1 ml-8 list-disc">
              <li>Ensure good lighting and clear image quality</li>
              <li>Position currency amounts prominently in the frame</li>
              <li>Works with receipts, bills, price tags, and digital displays</li>
              <li>Supported currencies: USD, EUR, GBP, JPY, CNY, and more</li>
            </ul>
          </div>}

        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>;
}